import { getCreatorPackageDealPriceState } from './dealPrice';
import type { CreatorContentTypeDisplayMode } from './contentTypePreview';

export type CreatorOrderRecord = Record<string, any>;

export const CREATOR_MULTI_PLATFORM_PLACEMENT_QUERY_KEY =
  'multiPlatformPlacement';

export type CreatorPlacement = {
  id: string;
  channelKey?: string;
  platform: string;
  platformId?: string;
  userName?: string;
  contentType?: string;
  allowedContentTypes?: string[];
  rate?: number;
  originalRate?: number;
  serviceFee?: number;
  actualGet?: number;
  deliveryRevision?: number;
  raw: CreatorOrderRecord;
};

export type CreatorCollaborationPackage = {
  id: string;
  primaryOrderId: string;
  primaryChannelKey?: string;
  packageRate?: number;
  originalRate?: number;
  serviceFee?: number;
  actualGet?: number;
  discountRate?: number;
  placements: CreatorPlacement[];
  raw: CreatorOrderRecord;
};

export type CreatorPlacementAccountConflict = {
  placement: CreatorPlacement;
  ownerEmail?: string;
};

const PACKAGE_KEYS = [
  'multiPlatformCollaboration',
  'collaborationPackage',
  'orderPackage'
] as const;

const PLACEMENT_KEYS = [
  'placements',
  'placementOrders',
  'platformOrders',
  'childOrders'
] as const;

const CHANNELS_KEY = 'channels';

const PLACEMENT_SHARED_PARENT_KEYS = [
  'campaign',
  'campaignId',
  'currency',
  'influencer',
  'isFirstOrder',
  'marketingStrategy',
  'product',
  'user'
] as const;

const firstPresent = (...values: any[]) =>
  values.find((value) => value !== undefined && value !== null && value !== '');

const asFiniteNumber = (value: any): number | undefined => {
  if (value === undefined || value === null || value === '') return undefined;
  const normalized =
    typeof value === 'string' ? value.replace(/[$,%\s,]/g, '') : value;
  const numericValue = Number(normalized);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

const normalizePlatform = (value: any) =>
  value === undefined || value === null
    ? ''
    : String(value).trim().toLowerCase();

const sumAllDefined = (values: Array<number | undefined>) =>
  values.length > 0 && values.every((value) => value !== undefined)
    ? (values as number[]).reduce((total, value) => total + value, 0)
    : undefined;

const roundMoney = (value: number) => Math.round(value * 100) / 100;

const getOrderId = (record: CreatorOrderRecord) =>
  String(firstPresent(record?.id, record?._id, record?.orderId, ''));

const getPackageContainer = (record: CreatorOrderRecord) => {
  for (const key of PACKAGE_KEYS) {
    const value = record?.[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      return value as CreatorOrderRecord;
    }
  }
  return undefined;
};

const withoutUndefinedValues = (record: CreatorOrderRecord = {}) =>
  Object.fromEntries(
    Object.entries(record).filter(([, value]) => value !== undefined)
  );

const getPlacementRecords = (record: CreatorOrderRecord) => {
  const container = getPackageContainer(record);
  const sources = [container, record].filter(Boolean) as CreatorOrderRecord[];

  for (const source of sources) {
    if (Array.isArray(source?.[CHANNELS_KEY])) {
      return source[CHANNELS_KEY].filter(Boolean);
    }
    for (const key of PLACEMENT_KEYS) {
      if (Array.isArray(source?.[key])) {
        return source[key]
          .map((placement: any) =>
            placement?.order
              ? {
                  ...placement,
                  ...withoutUndefinedValues(placement.order)
                }
              : placement
          )
          .filter(Boolean);
      }
    }
  }
  return [];
};

const getPriceField = (record: CreatorOrderRecord) =>
  record?.interestedPriceFields?.[0] ?? record?.priceFields?.[0] ?? {};

const normalizePlacement = (
  placementRecord: CreatorOrderRecord,
  parent: CreatorOrderRecord,
  index: number
): CreatorPlacement => {
  const sharedParent = Object.fromEntries(
    PLACEMENT_SHARED_PARENT_KEYS.flatMap((key) =>
      parent?.[key] === undefined ? [] : [[key, parent[key]]]
    )
  );
  const raw = { ...sharedParent, ...placementRecord };
  for (const key of [...PACKAGE_KEYS, ...PLACEMENT_KEYS, CHANNELS_KEY]) {
    delete raw[key];
  }
  const priceField = getPriceField(raw);
  const channelKey = firstPresent(raw?.channelKey, raw?.id, raw?._id);
  return {
    id: channelKey ? String(channelKey) : `placement-${index}`,
    channelKey: raw?.channelKey ? String(raw.channelKey) : undefined,
    platform: normalizePlatform(raw?.platform),
    platformId: raw?.platformId ? String(raw.platformId) : undefined,
    userName: firstPresent(raw?.userName, raw?.username, raw?.handle),
    contentType: firstPresent(
      raw?.contentType,
      raw?.placement,
      priceField?.contentType
    ),
    allowedContentTypes: Array.isArray(raw?.allowedContentTypes)
      ? raw.allowedContentTypes
          .map((contentType: unknown) => String(contentType).trim())
          .filter(Boolean)
      : undefined,
    rate: asFiniteNumber(
      firstPresent(
        raw?.placementRate,
        raw?.price,
        raw?.dealPrice,
        priceField?.price,
        priceField?.amount
      )
    ),
    originalRate: asFiniteNumber(
      firstPresent(
        raw?.placementOriginalRate,
        raw?.originalPrice,
        priceField?.originalPrice,
        priceField?.originPrice,
        priceField?.price
      )
    ),
    serviceFee: asFiniteNumber(
      firstPresent(
        raw?.serviceFee,
        priceField?.serviceFee,
        priceField?.talentManagerCost
      )
    ),
    actualGet: asFiniteNumber(
      firstPresent(raw?.actualGet, priceField?.actualGet)
    ),
    deliveryRevision: asFiniteNumber(raw?.deliveryRevision),
    raw
  };
};

const hasMultiplePlatforms = (placements: CreatorPlacement[]) =>
  new Set(placements.map((placement) => placement.platform).filter(Boolean))
    .size > 1;

const ACCOUNT_CONFLICT_STATUSES = new Set([
  'account_bound_to_another_user',
  'already_bound',
  'bound_by_other',
  'locked_by_other'
]);

export const getCreatorPackageAccountConflicts = (
  collaborationPackage: CreatorCollaborationPackage | null
): CreatorPlacementAccountConflict[] => {
  if (!collaborationPackage) return [];

  return collaborationPackage.placements.flatMap((placement) => {
    const raw = placement.raw ?? {};
    const status = String(
      firstPresent(
        raw.disabledReason,
        raw.accountOwnershipStatus,
        raw.accountBindingStatus,
        raw.bindingStatus,
        raw.status
      ) ?? ''
    )
      .trim()
      .toLowerCase();
    const ownerEmail = firstPresent(
      raw.boundOwnerEmail,
      raw.linkedAccountMaskedEmail
    );
    const hasConflict =
      Boolean(raw.accountOwnershipConflict) ||
      Boolean(raw.accountBoundToAnotherUser) ||
      Boolean(ownerEmail) ||
      ACCOUNT_CONFLICT_STATUSES.has(status);

    return hasConflict
      ? [
          {
            placement,
            ownerEmail: ownerEmail ? String(ownerEmail) : undefined
          }
        ]
      : [];
  });
};

/** Shared view model for the unified product-order DTO used by PC and H5. */
export const getCreatorCollaborationPackage = (
  order: CreatorOrderRecord
): CreatorCollaborationPackage | null => {
  if (!order) return null;
  const usesV2Channels = Array.isArray(order?.channels);
  const container = getPackageContainer(order) ?? {};
  const records = getPlacementRecords(order);
  const placements = records.map((record, index) =>
    normalizePlacement(record, order, index)
  );
  if (usesV2Channels) {
    if (placements.length < 1) return null;
  } else if (placements.length < 2 || !hasMultiplePlatforms(placements)) {
    return null;
  }

  const explicitPrimaryId = firstPresent(
    order?.displayChannelKey,
    container?.primaryChannelKey,
    container?.primaryOrderId,
    order?.primaryOrderId,
    order?.mainPlacementId
  );
  const primary =
    placements.find(
      (placement) => placement.id === String(explicitPrimaryId ?? '')
    ) ?? placements[0];
  const pricedPlacements = usesV2Channels
    ? placements.filter((placement) => placement.raw.active !== false)
    : placements;
  const placementRateTotal = sumAllDefined(
    pricedPlacements.map((placement) => placement.rate)
  );
  const placementOriginalTotal = sumAllDefined(
    pricedPlacements.map(
      (placement) => placement.originalRate ?? placement.rate
    )
  );
  const serviceFeeTotal = sumAllDefined(
    pricedPlacements.map((placement) => placement.serviceFee)
  );
  const actualGetTotal = sumAllDefined(
    pricedPlacements.map((placement) => placement.actualGet)
  );
  const packageRate = asFiniteNumber(
    firstPresent(
      container?.packageRate,
      container?.packagePrice,
      container?.price,
      order?.packageRate,
      order?.packagePrice,
      usesV2Channels ? order?.price : undefined,
      usesV2Channels ? getPriceField(order)?.price : undefined,
      placementRateTotal
    )
  );
  const originalRate = asFiniteNumber(
    firstPresent(
      container?.originalRate,
      container?.originalPrice,
      order?.packageOriginalRate,
      order?.originalTotalPrice,
      usesV2Channels ? order?.originalPrice : undefined,
      placementOriginalTotal
    )
  );
  const explicitDiscount = asFiniteNumber(
    firstPresent(
      container?.discountRate,
      order?.packageDiscountRate,
      order?.discountRate
    )
  );
  const discountRate =
    explicitDiscount !== undefined
      ? explicitDiscount > 1
        ? explicitDiscount / 100
        : explicitDiscount
      : packageRate !== undefined && originalRate && originalRate > packageRate
        ? (originalRate - packageRate) / originalRate
        : undefined;
  const childAmountScale =
    packageRate !== undefined &&
    placementRateTotal !== undefined &&
    placementRateTotal > 0
      ? packageRate / placementRateTotal
      : 1;
  const scaledServiceFeeTotal =
    serviceFeeTotal === undefined
      ? undefined
      : roundMoney(serviceFeeTotal * childAmountScale);
  const scaledActualGetTotal =
    actualGetTotal === undefined
      ? undefined
      : roundMoney(actualGetTotal * childAmountScale);
  const explicitServiceFee = asFiniteNumber(
    firstPresent(
      container?.serviceFee,
      container?.platformServiceFee,
      order?.packageServiceFee,
      usesV2Channels ? order?.serviceFee : undefined,
      usesV2Channels ? order?.platformServiceFee : undefined
    )
  );
  // V2 detail projects quote fees on the order's interestedPriceFields,
  // not necessarily on channels. Sum the complete quote once at package level;
  // never copy these fields onto every channel or treat missing fees as zero.
  const collectedServiceFeeTotal =
    usesV2Channels && Array.isArray(order?.interestedPriceFields)
      ? sumAllDefined(
          order.interestedPriceFields.map((field: CreatorOrderRecord) =>
            asFiniteNumber(field?.serviceFeeRate)
          )
        )
      : undefined;
  const quoteServiceFeeFallbackTotal =
    usesV2Channels && Array.isArray(order?.interestedPriceFields)
      ? sumAllDefined(
          order.interestedPriceFields.map((field: CreatorOrderRecord) => {
            const explicitFee = asFiniteNumber(field?.serviceFee);
            if (explicitFee !== undefined) return explicitFee;
            return asFiniteNumber(field?.talentManagerCost);
          })
        )
      : undefined;
  const serviceFee =
    usesV2Channels && collectedServiceFeeTotal !== undefined
      ? roundMoney(collectedServiceFeeTotal)
      : (explicitServiceFee ??
        (usesV2Channels
          ? (serviceFeeTotal ??
            (quoteServiceFeeFallbackTotal === undefined
              ? undefined
              : roundMoney(quoteServiceFeeFallbackTotal)))
          : scaledServiceFeeTotal));
  const explicitActualGet = asFiniteNumber(
    firstPresent(
      container?.actualGet,
      container?.creatorActualGet,
      order?.packageActualGet,
      usesV2Channels ? order?.actualGet : undefined,
      usesV2Channels ? order?.creatorActualGet : undefined,
      // This branch's creator price is already net; never deduct a guessed fee.
      usesV2Channels ? order?.price : undefined
    )
  );
  const actualGet = asFiniteNumber(
    firstPresent(
      explicitActualGet,
      packageRate !== undefined && explicitServiceFee !== undefined
        ? roundMoney(packageRate - explicitServiceFee)
        : undefined,
      usesV2Channels ? actualGetTotal : scaledActualGetTotal,
      packageRate !== undefined && serviceFee !== undefined
        ? roundMoney(packageRate - serviceFee)
        : undefined
    )
  );

  return {
    id: String(
      firstPresent(
        container?.id,
        container?._id,
        order?.collaborationPackageId,
        order?.packageId,
        order?.mainOrderId,
        getOrderId(order)
      )
    ),
    primaryOrderId: primary.id,
    primaryChannelKey: primary.channelKey,
    packageRate,
    originalRate,
    serviceFee,
    actualGet,
    discountRate,
    placements,
    raw: order
  };
};

export type MultiPlatformPresentation = {
  isMultiPlatform: boolean;
  platforms: string[];
  contentTypeMode?: CreatorContentTypeDisplayMode;
  placements: Array<{
    id: string;
    platform: string;
    contentType?: string;
    allowedContentTypes?: string[];
    priceFields?: Array<{ contentType?: string }>;
    active?: boolean;
    userName?: string;
  }>;
};

export const getMultiPlatformPresentation = (
  order: CreatorOrderRecord
): MultiPlatformPresentation => {
  const collaborationPackage = getCreatorCollaborationPackage(order);
  if (!collaborationPackage) {
    return {
      isMultiPlatform: false,
      platforms: order?.platform ? [normalizePlatform(order.platform)] : [],
      placements: order?.platform
        ? [
            {
              id: getOrderId(order),
              platform: normalizePlatform(order.platform),
              contentType: firstPresent(
                order?.contentType,
                getPriceField(order)?.contentType
              ),
              userName: firstPresent(
                order?.userName,
                order?.username,
                order?.handle
              )
            }
          ]
        : []
    };
  }

  return {
    isMultiPlatform:
      collaborationPackage.placements.length > 1 &&
      hasMultiplePlatforms(collaborationPackage.placements),
    contentTypeMode:
      getCreatorPackageDealPriceState(collaborationPackage) === 'initial'
        ? 'available'
        : 'selected',
    platforms: Array.from(
      new Set(
        collaborationPackage.placements.map((placement) => placement.platform)
      )
    ),
    placements: collaborationPackage.placements.map((placement) => ({
      id: placement.id,
      platform: placement.platform,
      contentType: placement.contentType,
      allowedContentTypes: placement.allowedContentTypes,
      priceFields: Array.isArray(placement.raw?.priceFields)
        ? placement.raw.priceFields
        : undefined,
      active: placement.raw?.active,
      userName: placement.userName
    }))
  };
};
